import { Nullable } from "sonobello.utilities.react";

import INoteTag from "./INoteTag";

/** The composed properties of content that will be appended to a note, and its shorthand expression in the UI.  */
export interface INoteTagConfiguration<TTemplateProps> {
  /** The shorthand expression of the tag, which is shown in the UI. If null, no indicator of the note tag will be
   * shown to the user.
   */
  readonly label: Nullable<string>;

  /** A lambda which transforms input properties into a string value to be appended to a note's content.
   * If null is returned, no content will be appended for the note.
   */
  readonly template: (templateProps: TTemplateProps) => Nullable<string>;
}

export interface INoteTagBuilder<TTemplateProps> {
  /** Constructs the note tags for the given input properties. */
  readonly getTags: (templateProps: TTemplateProps) => INoteTag[];
}

/**
 * Builds {@link INoteTag} from the given inputs in the same series as configured.
 */
export class SeriesNoteTagsBuilder<TTemplateProps> implements INoteTagBuilder<TTemplateProps> {
  private tagConfigurations: INoteTagConfiguration<TTemplateProps>[];

  constructor(tagConfigurations: INoteTagConfiguration<TTemplateProps>[]) {
    this.tagConfigurations = tagConfigurations;
  }

  /** Constructs the note tags from a configuration for the given input properties. */
  getTags = (templateProps: TTemplateProps): INoteTag[] => {
    const tags: INoteTag[] = [];
    this.tagConfigurations.forEach(({ label, template }) => {
      const value = template(templateProps);
      if (value) tags.push({ label, value });
    });
    return tags;
  };
}
